import React, { useState, useCallback } from "react";
import Lucide from "src/base-components/Lucide";
import { downloadPubNubFile, fetchPubNubFile } from "./pubnubFileUtils";
import PubNub from "pubnub";

// ─── Props ───
interface LazyFileAttachmentProps {
  pubnub: PubNub;
  channelId: string;
  file: { id: string; name: string; type: string; size: number };
  messageId: string;
  isMine: boolean;
}

// ─── Helpers ───
const formatFileSize = (bytes: number): string => {
  if (!bytes || bytes === 0) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${units[i]}`;
};

const getFileIcon = (type: string): string => {
  if (type?.startsWith("image/")) return "Image";
  // VIDEO UPLOAD DISABLED: video icon mapping commented out
  // if (type?.startsWith("video/")) return "Video";
  if (type?.includes("pdf")) return "FileText";
  if (type?.includes("word") || type?.includes("doc")) return "FileText";
  if (type?.includes("sheet") || type?.includes("excel") || type?.includes("xls")) return "FileSpreadsheet";
  return "File";
};

const getFileLabel = (type: string): string => {
  if (type?.startsWith("image/")) return "Image";
  // VIDEO UPLOAD DISABLED: video label mapping commented out
  // if (type?.startsWith("video/")) return "Video";
  if (type?.includes("pdf")) return "PDF";
  if (type?.includes("word") || type?.includes("doc")) return "Document";
  if (type?.includes("sheet") || type?.includes("excel")) return "Spreadsheet";
  return "File";
};

// ─── Component ───
const LazyFileAttachment: React.FC<LazyFileAttachmentProps> = ({
  pubnub,
  channelId,
  file,
  messageId,
  isMine,
}) => {
  const [status, setStatus] = useState<"idle" | "loading" | "loaded" | "error">("idle");
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const isImage = file.type?.startsWith("image/");
  // VIDEO UPLOAD DISABLED: video detection disabled
  const isVideo = false; // file.type?.startsWith("video/");

  // Fetch file and show preview (for images/videos) or trigger download (for docs)
  const handleDownload = useCallback(async () => {
    if (status === "loading") return;

    // For documents — directly trigger download without preview
    if (!isImage && !isVideo) {
      setStatus("loading");
      try {
        await downloadPubNubFile(pubnub, channelId, file.id, file.name);
        setStatus("idle");
      } catch {
        setStatus("error");
      }
      return;
    }

    // For images/videos — fetch blob URL for preview
    if (previewUrl) {
      // Already loaded → just open lightbox for images or download for videos
      if (isImage) {
        setLightboxOpen(true);
      } else {
        await downloadPubNubFile(pubnub, channelId, file.id, file.name);
      }
      return;
    }

    setStatus("loading");
    try {
      const blobUrl = await fetchPubNubFile(pubnub, channelId, file.id, file.name);
      setPreviewUrl(blobUrl);
      setStatus("loaded");
    } catch {
      setStatus("error");
    }
  }, [pubnub, channelId, file, status, previewUrl, isImage, isVideo]);

  // Download the actual file (after preview is loaded)
  const handleFileDownload = useCallback(async () => {
    try {
      await downloadPubNubFile(pubnub, channelId, file.id, file.name);
    } catch {
      console.error("Download failed");
    }
  }, [pubnub, channelId, file]);

  // ─── Loaded image preview ───
  if (isImage && status === "loaded" && previewUrl) {
    return (
      <>
        <div className="relative group/img rounded-lg overflow-hidden" style={{ maxWidth: 280 }}>
          <img
            src={previewUrl}
            alt={file.name}
            className="w-full max-h-52 object-cover cursor-pointer"
            onClick={() => setLightboxOpen(true)}
          />
          {/* Hover overlay with file name + download */}
          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent px-2 py-1.5 opacity-0 group-hover/img:opacity-100 transition-opacity flex items-end justify-between">
            <span className="text-white text-[11px] truncate max-w-[70%]">{file.name}</span>
            <button
              onClick={(e) => { e.stopPropagation(); handleFileDownload(); }}
              className="p-1.5 bg-white/20 backdrop-blur rounded-full hover:bg-white/40 transition-colors"
              title="Download"
            >
              <Lucide icon="Download" className="w-3.5 h-3.5 text-white" />
            </button>
          </div>
        </div>

        {/* Lightbox */}
        {lightboxOpen && (
          <div
            className="fixed inset-0 z-[9999] bg-black/90 flex items-center justify-center p-4"
            onClick={() => setLightboxOpen(false)}
          >
            <button
              className="absolute top-4 right-4 text-white hover:text-gray-300 z-10"
              onClick={() => setLightboxOpen(false)}
            >
              <Lucide icon="X" className="w-8 h-8" />
            </button>
            <button
              className="absolute top-4 left-4 flex items-center gap-2 text-white hover:text-gray-300 z-10"
              onClick={(e) => { e.stopPropagation(); handleFileDownload(); }}
            >
              <Lucide icon="Download" className="w-6 h-6" />
              <span className="text-sm">Download</span>
            </button>
            <img
              src={previewUrl}
              className="max-w-full max-h-full object-contain rounded-lg"
              alt={file.name}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        )}
      </>
    );
  }

  // ─── Loaded video preview ─── VIDEO UPLOAD DISABLED: video preview/player commented out
  // if (isVideo && status === "loaded" && previewUrl) {
  //   return (
  //     <div className="relative group/vid rounded-lg overflow-hidden" style={{ maxWidth: 320 }}>
  //       <video src={previewUrl} controls className="w-full max-h-56 rounded-lg" />
  //       <button
  //         onClick={handleFileDownload}
  //         className="absolute bottom-2 right-2 p-1.5 bg-black/50 backdrop-blur rounded-full opacity-0 group-hover/vid:opacity-100 transition-opacity hover:bg-black/70"
  //         title="Download"
  //       >
  //         <Lucide icon="Download" className="w-3.5 h-3.5 text-white" />
  //       </button>
  //     </div>
  //   );
  // }

  // ─── Placeholder UI (idle / loading / error) ───
  const bgColor = isMine ? "bg-white/15" : "bg-gray-50";
  const textColor = isMine ? "text-white" : "text-gray-700";
  const subTextColor = isMine ? "text-white/60" : "text-gray-400";
  const iconBg = isMine ? "bg-white/20" : "bg-gray-100";
  const btnColor = isMine
    ? "bg-white/20 hover:bg-white/30 text-white"
    : "bg-purple-50 hover:bg-purple-100 text-purple-600";

  return (
    <div
      className={`flex items-center gap-3 p-3 rounded-lg ${bgColor} cursor-pointer transition-all hover:opacity-90 min-w-[200px] max-w-full`}
      onClick={handleDownload}
    >
      {/* File type icon */}
      <div className={`flex-shrink-0 p-2.5 rounded-lg ${iconBg}`}>
        <Lucide
          icon={getFileIcon(file.type) as any}
          className={`w-6 h-6 ${isMine ? "text-white/80" : "text-purple-600"}`}
        />
      </div>

      {/* File info */}
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-medium truncate ${textColor}`}>{file.name}</p>
        <p className={`text-xs mt-0.5 ${subTextColor}`}>
          {getFileLabel(file.type)} · {formatFileSize(file.size)}
        </p>
      </div>

      {/* Action button */}
      <div className="flex-shrink-0">
        {status === "loading" ? (
          <div className={`p-2 rounded-full ${iconBg}`}>
            <Lucide icon="Loader2" className={`w-4 h-4 animate-spin ${isMine ? "text-white/70" : "text-purple-600"}`} />
          </div>
        ) : status === "error" ? (
          <button
            className={`p-2 rounded-full ${btnColor} transition-colors`}
            onClick={(e) => { e.stopPropagation(); setStatus("idle"); }}
            title="Retry"
          >
            <Lucide icon="RotateCw" className="w-4 h-4" />
          </button>
        ) : (
          <div className={`p-2 rounded-full ${iconBg}`}>
            <Lucide
              icon="Download"
              className={`w-4 h-4 ${isMine ? "text-white/70" : "text-purple-600"}`}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default LazyFileAttachment;
